import { useMemo, useRef, useState } from 'react';
import { Divider, Stack, Typography } from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';
import SearchInput from '../search-input/SearchInput';
import { MenuListAnnotationVars } from './MenuListAnnotationVars';
import { MenuListDatasetColumns } from './MenuListDatasetColumns';
import { MenuListFields } from './MenuListFields';
import { fuzzySearchMultipleWords } from './searchMatching';
import { SuggesterPopoverMode, VariableCompletion } from './types';

export type SuggesterSectionVariant = 'annotationVars' | 'fields' | 'dataset';

export type SuggesterSection = {
  key: string;
  title: string;
  items: VariableCompletion[];
  variant?: SuggesterSectionVariant;
};

export type SuggesterProps = {
  onInsert: (value: string) => void;
  mode?: SuggesterPopoverMode;
  sections: ReadonlyArray<SuggesterSection>;
};

export const Suggester = ({
  onInsert,
  mode = 'formula',
  sections,
}: SuggesterProps) => {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState<string>('');
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);
  const dummyRef = useRef<HTMLLIElement | null>(null);

  const visibleSections = useMemo(
    () =>
      sections
        .map(section => ({
          ...section,
          items: searchValue
            ? fuzzySearchMultipleWords(
                section.items,
                ['caption', 'value', 'description'],
                searchValue
              )
            : section.items,
        }))
        .filter(section => !searchValue || section.items.length > 0),
    [sections, searchValue]
  );

  const renderSection = (
    section: SuggesterSection,
    setFirstRef: React.MutableRefObject<HTMLLIElement | null>
  ) => {
    if (section.variant === 'fields') {
      return (
        <MenuListFields
          search={searchValue}
          fieldCompletions={section.items}
          onInsert={onInsert}
          setFirstRef={setFirstRef}
          mode={mode}
        />
      );
    }

    if (section.variant === 'dataset') {
      return (
        <MenuListDatasetColumns
          search={searchValue}
          completions={section.items}
          onInsert={onInsert}
          setFirstRef={setFirstRef}
          mode={mode}
        />
      );
    }

    return (
      <MenuListAnnotationVars
        search={searchValue}
        annotationVariables={section.items}
        onInsert={onInsert}
        mode={mode}
        setFirstRef={setFirstRef}
      />
    );
  };

  return (
    <Stack sx={{ background: 'inherit' }}>
      <Stack sx={{ p: 2 }}>
        <SearchInput
          onChange={setSearchValue}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') {
              firstMenuItemRef.current?.focus();
            }
          }}
          value={searchValue}
          placeholder={intl.formatMessage({
            id: 'features.formulas.suggester.search.placeholder',
          })}
          sx={{
            width: '100%',
          }}
          InputProps={{
            sx: {
              fontSize: ({ typography }) => typography.body2.fontSize,
            },
            autoComplete: 'off',
            autoFocus: true,
          }}
        />
      </Stack>

      {visibleSections.map((section, index) => {
        const isFirst = index === 0;
        const needsDivider = !isFirst;

        return (
          <div key={section.key} style={{ maxWidth: 400 }}>
            {needsDivider && <Divider />}
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="text.primary"
              sx={{ px: 2, pt: 1 }}
            >
              {section.title}
            </Typography>
            {renderSection(section, isFirst ? firstMenuItemRef : dummyRef)}
          </div>
        );
      })}
    </Stack>
  );
};
